import _ from "lodash";
import numeral from "numeral";

import {
  ActionsState,
  DealRoomAction,
  DealRoomChangeRequest,
  DealRoomMapActionTypes,
  DealRoomMapState,
  DealRoomSectionChangeRequest,
  DealRoomStage,
} from "src/types/dealRoom";
import { secondsToMinutes } from "./util";
import { Asset } from "src/types/assets";
import { nanoid } from "nanoid";

export function persistSortPreference(sortBy: string, orderBy: string) {
  const sort = {
    sortBy: sortBy,
    orderBy: orderBy,
  };

  window.localStorage.setItem("dealRoomSort", JSON.stringify(sort));
}

export function getSortPreference() {
  const sort = window.localStorage.getItem("dealRoomSort");
  return sort ? JSON.parse(sort) : { sortBy: "name", orderBy: "asc" };
}

export const shouldNotifyOnChange = (source, target) => {
  const sourceCollection = {
    id: _.get(source, "id"),
    name: _.get(source, "name"),
    assets: getAssetsInCollection(source),
    description: _.get(source, "description"),
  };

  const targetCollection = {
    id: _.get(target, "id"),
    name: _.get(target, "name"),
    assets: getAssetsInCollection(target),
    description: _.get(target, "description"),
  };

  return !_.isEqual(sourceCollection, targetCollection);
};

const getAssetsInCollection = (collection) => {
  const assetIds: string[] = [];

  const sections = _.get(collection, "sections");

  if (sections && sections.length > 0) {
    sections.forEach((section) => {
      if (section.loaded && section.item.assets) {
        section?.item?.assets?.items?.forEach((asset) => {
          assetIds.push(asset.id);
        });
      }
    });
  } else {
    const assets = _.get(collection, "assets");
    if (assets && assets.length > 0) {
      assets.forEach((asset) => {
        assetIds.push(asset.id);
      });
    }
  }

  return assetIds.sort();
};

export const calculateUpdateRequest = (source, target, ignoreNull = false) => {
  const changes: DealRoomChangeRequest = {};

  if (!_.isEqual(source.subject, target.subject) && target.subject !== "") {
    changes.name = target.subject;
  }

  if (!_.isEqual(source.description, target.description)) {
    changes.description = target.description;
  }

  if (!_.isEqual(source.meetingLink, target.meetingLink)) {
    changes.meetingLink = target.meetingLink;
  }

  if (!_.isEqual(source.banner, target.banner)) {
    changes.banner = { ...target.banner };
    const sourceBackground = _.get(source, "banner.background.id");
    const targetBackground = _.get(target, "banner.background.id");

    const sourceBackgroundType = _.get(source, "banner.type");
    const targetBackgroundType = _.get(target, "banner.filter");

    if (sourceBackgroundType !== targetBackgroundType) {
      _.set(changes, "banner.type", targetBackgroundType);
    }

    if (sourceBackground !== targetBackground) {
      _.set(changes, "banner.background", targetBackground);
    } else {
      _.set(changes, "banner.background", null);
    }
  }

  if (!_.isEqual(source.customerName, target.customerName)) {
    changes.customerName = target.customerName;
  }

  if (!_.isEqual(source.customerLogo, target.customerLogo)) {
    changes.customerLogo = _.get(target, "customerLogo.id") || null;
  }

  if (!_.isEqual(source.companyLogo, target.companyLogo)) {
    changes.companyLogo = _.get(target, "companyLogo.id") || null;
  }

  if (!_.isEqual(source.viewerType, target.viewerType)) {
    changes.viewerType = target.viewerType;
  }

  if (!_.isEqual(source.assetViewer, target.assetViewer)) {
    changes.assetViewer = target.assetViewer;
  }

  if (!_.isEqual(source.opportunity, target.opportunity)) {
    changes.opportunity = target.opportunity;
  }

  const sourceAssets = prepareAssetUpdate(source.assets || []);
  const targetAssets = prepareAssetUpdate(target.assets || []);
  if (!_.isEqual(sourceAssets, targetAssets)) {
    const deletedAssets = _.differenceBy(sourceAssets, targetAssets, "id");
    const deletedAssetsIds = deletedAssets.map((item) => _.get(item, "id", ""));
    if (_.size(deletedAssetsIds) > 0) {
      changes.deletedAssets = deletedAssetsIds;
    }

    // if target have section no need to send the assets
    if (_.isEmpty(_.get(target, "sections", []))) {
      changes.assets = targetAssets;
    }
  }

  if (target.sections && target.sections.length > 0) {
    const sourceSections = source.sections || [];

    const updatedSections = target.sections
      .map((section) => {
        //Find the section in source sections
        const sourceSection = sourceSections.find((item) => {
          return item.item?.id == section.item?.id;
        });

        return calculateSectionChanged(sourceSection, section, ignoreNull);
      })
      .filter((change) => change != null);
    if (_.size(updatedSections) > 0) {
      changes.sections = updatedSections;
    }
  }

  if (source.sections && source.sections.length > 0) {
    const targetSections = target.sections || [];

    const deletedSectionIds = source.sections
      .filter((section) => {
        const found = targetSections.find((item) => {
          return section.item?.id == item.item?.id;
        });

        return found == null;
      })
      .map((item) => item.item.id)
      .filter((item) => item != null);
    if (_.size(deletedSectionIds) > 0) {
      changes.deletedSections = deletedSectionIds;
    }
  }

  return changes;
};

const prepareAssetUpdate = (assets) => {
  if (!assets || _.size(assets) === 0) {
    return [];
  }

  return assets.map((asset, index) => {
    return {
      id: asset.id,
      order: index + 1,
      assetMetadata: asset.metadata,
    };
  });
};

export const calculateSectionChanged = (source, target, ignoreNull) => {
  const targetAssets = prepareAssetUpdate(target.item?.assets?.items || []);
  if (!source) {
    return {
      id: target.item?.id,
      name: target.item?.name,
      assets: targetAssets,
    };
  }

  const sourceAssets = prepareAssetUpdate(source.item?.assets?.items || []);

  const changes: DealRoomSectionChangeRequest = {
    id: target.item?.id,
  };

  if (!_.isEqual(source.item?.name, target.item?.name)) {
    if (target.item?.name && target.item?.name.trim() !== "") {
      changes.name = target.item?.name;
    }
  }

  if (!_.isEqual(sourceAssets, targetAssets)) {
    const deletedAssets = _.differenceBy(sourceAssets, targetAssets, "id");
    const deletedAssetsIds = deletedAssets.map((item) => _.get(item, "id", ""));
    if (_.size(deletedAssetsIds) > 0) {
      changes.deletedAssets = deletedAssetsIds;
    }

    changes.assets = targetAssets;
  }

  const isSectionUnchanged =
    !changes.name && !changes.assets && !changes.deletedAssets;
  if (ignoreNull && isSectionUnchanged) {
    return null;
  }
  return changes;
};

export enum SmartCardTypes {
  DEAL_PROBABILITY = "DEAL_PROBABILITY",
  ACTIVE_PEOPLE = "ACTIVE_PEOPLE",
  MESSAGE_LIST = "MESSAGE_LIST",
  PARTICIPANT_ENGAGEMENT = "PARTICIPANT_ENGAGEMENT",
  RECENT_UPLOADS = "RECENT_UPLOADS",
  NEW_PARTICIPANT = "NEW_PARTICIPANT",
  MAP_DEAL = "MAP_DEAL",
  DEALROOM_ENGAGEMENT = "DEALROOM_ENGAGEMENT",
}

export enum SmartCardsOrder {
  DEAL_PROBABILITY = 0,
  MAP_DEAL = 1,
  ACTIVE_PEOPLE = 2,
  MESSAGE_LIST = 3,
  PARTICIPANT_ENGAGEMENT = 4,
  RECENT_UPLOADS = 5,
  NEW_PARTICIPANT = 6,
}

export enum DefaultSmartCardTypes {
  DEAL_PROBABILITY = "DEAL_PROBABILITY",
  ACTIVE_PEOPLE = "ACTIVE_PEOPLE",
  MESSAGE_LIST = "MESSAGE_LIST",
  MAP_DEAL = "MAP_DEAL",
}

const getDefaultSmartCards = (
  isDealInsightsEnabled: boolean,
  isDealMapped: boolean,
) => {
  return [
    {
      type: SmartCardTypes.DEAL_PROBABILITY,
      title: isDealInsightsEnabled
        ? "Deal Insights is ready"
        : "View Deal Insights",
      subtext: isDealInsightsEnabled
        ? "Map your deal to see Win Probability and Pipeline Progress."
        : "See win probability and pipeline progress — ask us how!",
      ctaText: isDealInsightsEnabled ? "Map your deal" : "Learn more",
    },
    {
      type: SmartCardTypes.MAP_DEAL,
      title: isDealMapped ? "Update deal" : "Map Your Deal",
      subtext: isDealMapped
        ? "Switch to a different deal to match the right opportunity."
        : "Connect to your CRM & map your deal to see all the related insights in one place.",
      ctaText: isDealMapped ? "Update now" : "Map your deal now",
    },
    {
      type: SmartCardTypes.ACTIVE_PEOPLE,
      title:
        "No one is active right now. Leave a message to engage them when they log in.",
      subtext: "",
      ctaText: "Engage",
    },
    {
      type: SmartCardTypes.MESSAGE_LIST,
      title: "No new messages. You're all caught up!",
      subtext: "",
      ctaText: "",
    },
  ];
};

export const createdBy = (assetItem: Asset) => {
  return (
    _.get(assetItem, "author.firstName") ||
    _.get(assetItem, "createdBy.firstName") ||
    ""
  );
};

export const transformSmartCards = (
  cards: any[],
  isDealMapped: boolean,
  isDealInsightsEnabled: boolean,
  dealRoomOwner: string,
  isEditing: boolean,
) => {
  const defaultSmartCardsToBeAppended = getDefaultSmartCards(
    isDealInsightsEnabled,
    isDealMapped,
  ).filter((card) => {
    return !_.some(cards, { type: card.type });
  });

  if (isEditing) {
    return [defaultSmartCardsToBeAppended[1]];
  }

  cards = _.map(cards, (card) => {
    const type = _.get(card, "type", "");

    const doubleWidth =
      type === SmartCardTypes.RECENT_UPLOADS ||
      (type === SmartCardTypes.MESSAGE_LIST &&
        _.get(card, "unReadMessages", 0) > 0) ||
      type === SmartCardTypes.PARTICIPANT_ENGAGEMENT ||
      type === SmartCardTypes.NEW_PARTICIPANT;

    const getSmartCardContent = () => {
      switch (type) {
        case SmartCardTypes.DEAL_PROBABILITY:
          return {
            title: isDealInsightsEnabled
              ? `${numeral(_.get(card, "prediction", 0)).format("(0)")}%`
              : "",
            subtext: isDealInsightsEnabled ? `${_.get(card, "stage")}` : "",
            ctaText: isDealInsightsEnabled
              ? "More details"
              : "Click here to know more",
          };

        case SmartCardTypes.ACTIVE_PEOPLE:
          let activePeopleSubtext = "";
          let activePeopleCtaText = "Chat now";
          const activePeopleCard = _.find(cards, {
            type: SmartCardTypes.ACTIVE_PEOPLE,
          });
          const activePeople = _.get(activePeopleCard, "chatUsers", []);
          if (_.size(activePeople) > 1) {
            activePeopleSubtext = `${_.get(activePeople[0], "firstName")} & ${_.size(activePeople) - 1} other people are active now, start a conversation`;
          } else if (_.size(activePeople) === 1) {
            activePeopleSubtext = `${_.get(activePeople[0], "firstName")} is active now, start a conversation`;
          } else {
            activePeopleSubtext = "No one is online right now!";
            activePeopleCtaText = "Start a conversation";
          }

          return {
            title: "",
            ctaText: activePeopleCtaText,
            subtext: activePeopleSubtext,
          };

        case SmartCardTypes.MESSAGE_LIST:
          return {
            title: `You've got new messages! (${_.get(card, "unReadMessages", 0)})`,
            ctaText: "View now",
            subtext: "Don't miss out on important conversations!",
          };

        case SmartCardTypes.PARTICIPANT_ENGAGEMENT:
          return {
            title: `Participants spent ~${secondsToMinutes(_.get(card, "timeSpent", 0))}m on your recently uploaded asset`,
            ctaText: "More details",
            subtext: "",
          };

        case SmartCardTypes.RECENT_UPLOADS:
          return {
            title: `Recently uploaded assets (${_.get(card, "assetCount", 0)})`,
            ctaText: "More details",
            subtext: "",
          };

        case SmartCardTypes.NEW_PARTICIPANT:
          let newParticipantSubtext = `${_.get(card, "totalPeople")} user${_.get(card, "totalPeople") > 1 ? "s were" : " was"} recently added by ${dealRoomOwner ?? "Deal Room Owner"}`;
          return {
            title: "New external stakeholders joined!",
            ctaText: "View details",
            subtext: newParticipantSubtext,
          };

        default:
          return null;
      }
    };

    const smartCardContent = getSmartCardContent();

    return {
      width: doubleWidth ? 2 : 1,
      height: 1,
      ...smartCardContent,
      ...card,
    };
  });

  cards = [...defaultSmartCardsToBeAppended, ...cards];

  const sortedCards = cards.sort((a, b) => {
    return (
      SmartCardsOrder[a.type as keyof typeof SmartCardsOrder] -
      SmartCardsOrder[b.type as keyof typeof SmartCardsOrder]
    );
  });
  return sortedCards;
};

// DEAL ROOM MAP
const paginationInitialState = {
  currentPage: -1,
  totalPages: 0,
  recordsPerPage: 20,
  totalRecordsCount: 0,
  hasMoreItems: false,
};

export const NEW_ACTION_INITIAL_STATE = {
  id: nanoid(6),
  actionItem: "",
  assets: [],
  recipients: [],
  dueDate: null,
  completed: false,
  stage: "",
};

export const dealRoomMapInitialState: DealRoomMapState = {
  editMode: false,
  editingItem: null,
  addAssetSlider: {
    show: false,
    stageId: "",
    actionId: "",
    isNewAction: false,
    existingAssets: [],
  },
  showAddAssetSlider: false,
  saveActionSlider: {
    stageId: "",
    action: null,
    changes: {},
    stakeholdersToBeNotified: [],
  },
  deleteActionSlider: {
    stageId: "",
    action: null,
  },
  isReorderingStages: false,
  deletingStage: {
    id: null,
    loading: false,
    error: null,
  },
  editingStage: {
    id: null,
    item: null,
    loading: false,
    error: null,
  },
  creatingStage: {
    id: null,
    loading: false,
    error: null,
  },
  stages: {
    loading: false,
    loaded: false,
    items: [],
    oldStageOrder: null,
    error: null,
  },
  actions: {},
  actionsProgress: {
    data: null,
    loading: false,
    error: null,
  },
  recipients: {
    items: [],
    pagination: paginationInitialState,
    loaded: false,
    loading: false,
    error: null,
  },
  assets: {
    items: [],
    pagination: paginationInitialState,
    loaded: false,
    loading: false,
    error: null,
  },
  completeAction: {
    stageId: "",
    actionId: "",
    completed: false,
    loading: false,
    error: null,
  },
  deleteAction: {
    stageId: "",
    actionId: "",
    loading: false,
    error: null,
  },
};

export function dealRoomMapReducer(
  state: DealRoomMapState,
  action: DealRoomMapActionTypes,
): DealRoomMapState {
  switch (action.type) {
    case "TOGGLE_EDIT_MODE": {
      const editMode = _.get(action, "payload.editMode");

      return {
        ...state,
        editMode,
      };
    }

    case "FETCH_ACTIONS_PROGRESS": {
      return {
        ...state,
        actionsProgress: {
          ..._.get(state, "actionsProgress"),
          loading: true,
        },
      };
    }

    case "FETCHING_ACTIONS_PROGRESS_SUCCEEDED": {
      const data = _.get(action, "payload.progress");
      if (!data) {
        return {
          ...state,
        };
      }

      return {
        ...state,
        actionsProgress: {
          ..._.get(state, "actionsProgress"),
          data: {
            ...data,
          },
          loading: false,
        },
      };
    }

    case "FETCHING_ACTIONS_PROGRESS_FAILED": {
      const error = _.get(action, "payload.error");

      return {
        ...state,
        actionsProgress: {
          ..._.get(state, "actionsProgress"),
          loading: false,
          error,
        },
      };
    }

    case "FETCH_RECIPIENTS_FOR_MAP": {
      return {
        ...state,
        recipients: {
          ..._.get(state, "recipients"),
          loading: true,
        },
      };
    }

    case "FETCHING_RECIPIENTS_FOR_MAP_SUCCEEDED": {
      const response = _.get(action, "payload.recipients");

      return {
        ...state,
        recipients: {
          ..._.get(state, "recipients"),
          items: _.get(response, "records", []),
          loading: false,
          loaded: true,
          pagination: {
            currentPage: _.get(response, "pagination.pageNumber", -1),
            totalPages: _.get(response, "pagination.totalPagesCount", 0),
            recordsPerPage: _.get(response, "pagination.itemsPerPage", 20),
            totalRecordsCount: _.get(response, "pagination.totalItemCount", 0),
            hasMoreItems:
              _.get(response, "pagination.pageNumber", -1) + 1 <
              _.get(response, "pagination.totalPagesCount", 0),
          },
        },
      };
    }

    case "FETCHING_RECIPIENTS_FOR_MAP_FAILED": {
      const error = _.get(action, "payload.error");

      return {
        ...state,
        recipients: {
          ..._.get(state, "recipients"),
          loading: false,
          loaded: false,
          error,
        },
      };
    }

    case "FETCH_ASSETS_FOR_MAP": {
      return {
        ...state,
        assets: {
          ..._.get(state, "assets"),
          loading: true,
        },
      };
    }

    case "FETCHING_ASSETS_FOR_MAP_SUCCEEDED": {
      const response = _.get(action, "payload.assets");

      return {
        ...state,
        assets: {
          ..._.get(state, "assets"),
          items: [
            ..._.get(state, "assets.items", []),
            ..._.get(response, "records", []),
          ],
          loading: false,
          loaded: true,
          pagination: {
            currentPage: _.get(response, "pagination.pageNumber", -1),
            totalPages: _.get(response, "pagination.totalPagesCount", 0),
            recordsPerPage: _.get(response, "pagination.itemsPerPage", 20),
            totalRecordsCount: _.get(response, "pagination.totalItemCount", 0),
            hasMoreItems:
              _.get(response, "pagination.pageNumber", -1) + 1 <
              _.get(response, "pagination.totalPagesCount", 0),
          },
        },
      };
    }

    case "FETCHING_ASSETS_FOR_MAP_FAILED": {
      const error = _.get(action, "payload.error");

      return {
        ...state,
        assets: {
          ..._.get(state, "assets"),
          loading: false,
          loaded: false,
          error,
        },
      };
    }

    case "FETCH_STAGES": {
      const loadedAlready = _.get(state, "stages.loaded", false);

      if (loadedAlready) {
        return {
          ...state,
        };
      }

      return {
        ...state,
        stages: {
          ..._.get(state, "stages"),
          loading: true,
        },
      };
    }

    case "FETCHING_STAGES_SUCCEEDED": {
      const stages = _.get(action, "payload.stages", []);

      return {
        ...state,
        stages: {
          ..._.get(state, "stages"),
          items: stages,
          loading: false,
          loaded: true,
        },
      };
    }

    case "FETCHING_STAGES_FAILED": {
      const error = _.get(action, "payload.error");

      return {
        ...state,
        stages: {
          ..._.get(state, "stages"),
          loading: false,
          loaded: false,
          error,
        },
      };
    }

    case "ADD_NEW_STAGE": {
      return {
        ...state,
        creatingStage: {
          ..._.get(state, "creatingStage"),
          id: "new-stage",
        },
        stages: {
          ..._.get(state, "stages"),
          items: [
            ..._.get(state, "stages.items", []),
            {
              id: "new-stage",
              title: "",
              isStageAccordionOpen: false,
            },
          ],
        },
        editingItem: {
          type: "stage",
          id: "new-stage",
          wiggleKey: 0,
        },
      };
    }

    case "UPDATE_NEW_STAGE": {
      const stages = _.get(state, "stages.items", []);

      const updatedNewStage = _.find(
        stages,
        (stage) => _.get(stage, "id") === "new-stage",
      );
      if (!updatedNewStage) {
        return {
          ...state,
        };
      }

      updatedNewStage.title = _.get(action, "payload.newStageTitle", "");

      const allStagesExceptLast =
        _.size(stages) > 0 ? _.slice(stages, 0, _.size(stages) - 1) : [];
      return {
        ...state,
        stages: {
          ..._.get(state, "stages"),
          items: [...allStagesExceptLast, updatedNewStage],
        },
      };
    }

    case "TOGGLE_STAGE_ACCORDION": {
      const targetStageId = _.get(action, "payload.stageId");

      const stages = _.get(state, "stages.items", []);
      const targetStageIndex = _.findIndex(
        stages,
        (stage) => _.get(stage, "id") === targetStageId,
      );

      const targetStage = stages[targetStageIndex];

      if (!targetStageId || _.size(stages) === 0 || targetStageIndex < 0) {
        return {
          ...state,
        };
      }

      const modifiedStage = {
        ...targetStage,
        isStageAccordionOpen: !_.get(
          targetStage,
          "isStageAccordionOpen",
          false,
        ),
      };

      // modifying updated stage in place
      stages.splice(targetStageIndex, 1, modifiedStage);

      return {
        ...state,
        stages: {
          ..._.get(state, "stages"),
          items: [...stages],
        },
      };
    }

    case "CREATE_NEW_STAGE": {
      return {
        ...state,
        creatingStage: {
          ..._.get(state, "creatingStage"),
          loading: true,
        },
      };
    }

    case "CREATING_NEW_STAGE_SUCCEEDED": {
      const newStage = _.get(action, "payload.newStage");

      if (!newStage) {
        return {
          ...state,
        };
      }

      const updatedNewStage = {
        ...newStage,
        isStageAccordionOpen: true,
      };

      const stages = _.get(state, "stages.items", []);
      const allStagesExceptLast =
        _.size(stages) > 0 ? _.slice(stages, 0, _.size(stages) - 1) : [];

      return {
        ...state,
        creatingStage: {
          id: null,
          loading: false,
          error: null,
        },
        stages: {
          ..._.get(state, "stages"),
          items: [...allStagesExceptLast, updatedNewStage],
        },
        editingItem: null,
      };
    }

    case "CREATING_NEW_STAGE_FAILED": {
      const error = _.get(action, "payload.action");

      return {
        ...state,
        creatingStage: {
          id: null,
          loading: false,
          error,
        },
      };
    }

    case "INVALIDATE_NEW_STAGE": {
      const stages = _.get(state, "stages.items", []);
      const allStagesExceptLast =
        _.size(stages) > 0 ? _.slice(stages, 0, _.size(stages) - 1) : [];

      return {
        ...state,
        creatingStage: {
          id: null,
          loading: false,
          error: null,
        },
        stages: {
          ..._.get(state, "stages"),
          items: [...allStagesExceptLast],
        },
        editingItem: null,
      };
    }

    case "TOGGLE_EDIT_STAGE": {
      const targetStageId = _.get(action, "payload.stageId");
      const stages = _.get(state, "stages.items", []);

      const targetStage = _.find(stages, (stage) => stage.id === targetStageId);

      if (!targetStageId || !targetStage) {
        return {
          ...state,
        };
      }

      const editingItem = _.get(state, "editingItem");
      const creatingStage = _.get(state, "creatingStage");
      if (editingItem && targetStageId !== editingItem.id && creatingStage) {
        return {
          ...state,
          editingItem: {
            ..._.get(state, "editingItem"),
            wiggleKey: _.get(state, "editingItem.wiggleKey", 0) + 1,
          } as { type: "stage" | "action"; id: string; wiggleKey: number },
        };
      }

      return {
        ...state,
        editingStage: {
          id: targetStageId,
          item: targetStage,
          loading: false,
          error: null,
        },
        editingItem: {
          type: "stage",
          id: targetStageId,
          wiggleKey: 0,
        },
      };
    }

    case "INVALIDATE_EDIT_STAGE": {
      return {
        ...state,
        editingStage: {
          id: null,
          item: null,
          loading: false,
          error: null,
        },
        editingItem: null,
      };
    }

    case "EDIT_STAGE": {
      const stageId = _.get(action, "payload.stageId");
      const newTitle = _.get(action, "payload.newTitle");

      if (!stageId) {
        return {
          ...state,
        };
      }

      return {
        ...state,
        editingStage: {
          ..._.get(state, "editingStage"),
          item: {
            ..._.get(state, "editingStage.item"),
            title: newTitle,
          } as DealRoomStage,
        },
      };
    }

    case "UPDATE_STAGE": {
      return {
        ...state,
        editingStage: {
          ..._.get(state, "editingStage"),
          loading: true,
        },
      };
    }

    case "UPDATING_STAGE_SUCCEEDED": {
      const modifiedStage = _.get(action, "payload.modifiedStage");
      const stageId = _.get(modifiedStage, "id");

      const stages = _.get(state, "stages.items", []);
      const targetStageIndex = _.findIndex(
        stages,
        (stage) => _.get(stage, "id") === stageId,
      );

      if (!stageId || _.size(stages) === 0 || targetStageIndex < 0) {
        return {
          ...state,
        };
      }

      // modifying updated stage in place
      stages.splice(targetStageIndex, 1, modifiedStage);

      return {
        ...state,
        stages: {
          ..._.get(state, "stages"),
          items: [...stages],
        },
        editingStage: {
          id: null,
          item: null,
          loading: false,
          error: null,
        },
        editingItem: null,
      };
    }

    case "UPDATING_STAGE_FAILED": {
      const error = _.get(action, "payload.error");

      return {
        ...state,
        editingStage: {
          id: null,
          item: null,
          loading: false,
          error,
        },
      };
    }

    case "TOGGLE_DELETE_STAGE": {
      const stageId = _.get(action, "payload.stageId");

      return {
        ...state,
        deletingStage: {
          id: stageId,
          loading: false,
          error: null,
        },
      };
    }

    case "DELETE_STAGE": {
      return {
        ...state,
        deletingStage: {
          ..._.get(state, "deletingStage"),
          loading: true,
        },
      };
    }

    case "DELETING_STAGE_SUCCEEDED": {
      const stages = _.get(state, "stages.items");
      if (_.size(stages) === 0) {
        return {
          ...state,
        };
      }
      const updatedStages = _.remove(
        stages,
        (stage) => stage.id !== _.get(action, "payload.stageId"),
      );

      return {
        ...state,
        deletingStage: {
          id: null,
          loading: false,
          error: null,
        },
        stages: {
          ..._.get(state, "stages"),
          items: [...updatedStages],
        },
      };
    }

    case "DELETING_STAGE_FAILED": {
      const error = _.get(action, "payload.error");

      return {
        ...state,
        deletingStage: {
          id: null,
          loading: false,
          error,
        },
      };
    }

    case "INVALIDATE_DELETE_STAGE": {
      return {
        ...state,
        deletingStage: {
          id: null,
          loading: false,
          error: null,
        },
      };
    }

    case "UPDATE_STAGE_ORDER": {
      const updatedStageOrder = _.get(action, "payload", []);

      return {
        ...state,
        stages: {
          ..._.get(state, "stages"),
          items: [...updatedStageOrder],
        },
      };
    }

    case "UPDATING_STAGE_ORDER_SUCCEEDED": {
      const newStageOrder = _.get(action, "payload.stages", []);

      return {
        ...state,
        stages: {
          ..._.get(state, "stages"),
          items: [...newStageOrder],
        },
      };
    }

    case "UPDATING_STAGE_ORDER_FAILED": {
      const error = _.get(action, "payload.error");
      const oldStageOrder = _.get(state, "stages.oldStageOrder", []);

      return {
        ...state,
        stages: {
          ..._.get(state, "stages"),
          items: [...oldStageOrder],
          error,
        },
      };
    }

    case "FETCH_ACTIONS_FOR_STAGE": {
      const { stageId } = _.get(action, "payload");

      if (!stageId) {
        return {
          ...state,
        };
      }

      return {
        ...state,
        actions: {
          ..._.get(state, "actions"),
          [stageId]: {
            items: [..._.get(state, `actions[${stageId}].items`, [])],
            error: null,
            loaded: false,
            loading: true,
            pagination: {
              ...paginationInitialState,
            },
            oldOrder: null,
            newAction: {
              item: {
                ...NEW_ACTION_INITIAL_STATE,
                id: nanoid(6),
                stage: stageId,
              },
              loading: false,
              error: null,
            },
            editAction: {
              item: null,
              loading: false,
              error: null,
            },
          },
        },
      };
    }

    case "FETCHING_ACTIONS_FOR_STAGE_SUCCEEDED": {
      const { stageId, response } = _.get(action, "payload");

      if (!stageId) {
        return {
          ...state,
        };
      }

      const updatedActions = _.uniqBy(
        [
          ..._.get(state, `actions[${stageId}].items`, []),
          ..._.get(response, "actions", []),
        ],
        "id",
      );

      return {
        ...state,
        actions: {
          ..._.get(state, "actions"),
          [stageId]: {
            ..._.get(state, `actions[${stageId}]`),
            items: updatedActions,
            loaded: true,
            loading: false,
            pagination: {
              currentPage: _.get(response, "pagination.pageNumber", -1),
              totalPages: _.get(response, "pagination.totalPagesCount", 0),
              recordsPerPage: _.get(response, "pagination.itemsPerPage", 20),
              totalRecordsCount: _.get(
                response,
                "pagination.totalItemCount",
                0,
              ),
              hasMoreItems:
                _.get(response, "pagination.pageNumber", -1) + 1 <
                _.get(response, "pagination.totalPagesCount", 0),
            },
          },
        },
      };
    }

    case "FETCHING_ACTIONS_FOR_STAGE_FAILED": {
      const { stageId, error } = _.get(action, "payload");

      if (!stageId) {
        return {
          ...state,
        };
      }

      return {
        ...state,
        actions: {
          ..._.get(state, "actions"),
          [stageId]: {
            ..._.get(state, `actions[${stageId}]`),
            items: [],
            error: error,
            loaded: true,
            loading: false,
          },
        },
      };
    }

    case "CREATE_NEW_ACTION": {
      const stageId = _.get(action, "payload.stageId");

      if (!stageId) {
        return {
          ...state,
        };
      }

      return {
        ...state,
        actions: {
          ..._.get(state, "actions"),
          [stageId]: {
            ..._.get(state, `actions[${stageId}]`),
            newAction: {
              ..._.get(state, `actions[${stageId}].newAction`),
              loading: true,
            },
          },
        },
      };
    }

    case "CREATING_NEW_ACTION_SUCCEEDED": {
      const stageId = _.get(action, "payload.stageId");
      const updatedStageInfo = _.get(action, "payload.updatedStageInfo");

      if (!stageId) {
        return {
          ...state,
        };
      }

      const stages = _.get(state, "stages.items", []);
      const targetStageIndex = _.findIndex(
        stages,
        (stage) => stage.id === stageId,
      );

      if (targetStageIndex < 0) {
        return {
          ...state,
        };
      }

      const targetStage = _.get(state, `stages.items[${targetStageIndex}]`, {});
      const updatedTargetStage = {
        ...targetStage,
        ...updatedStageInfo,
      };

      const modifiedStages = _.map(stages, (stage) => {
        if (stage.id === stageId) {
          return updatedTargetStage;
        }

        return stage;
      });

      return {
        ...state,
        actions: {
          ..._.get(state, "actions"),
          [stageId]: {
            ..._.get(state, `actions[${stageId}]`),
            newAction: {
              item: {
                ...NEW_ACTION_INITIAL_STATE,
                id: nanoid(6),
                stage: stageId,
              },
              loading: false,
              error: null,
            },
          },
        },
        stages: {
          ..._.get(state, "stages"),
          items: modifiedStages,
        },
        editingItem: null,
      };
    }

    case "CREATING_NEW_ACTION_FAILED": {
      const stageId = _.get(action, "payload.stageId");
      const error = _.get(action, "payload.error");

      if (!stageId) {
        return {
          ...state,
        };
      }

      return {
        ...state,
        actions: {
          ..._.get(state, "actions"),
          [stageId]: {
            ..._.get(state, `actions[${stageId}]`),
            newAction: {
              ..._.get(state, `actions[${stageId}].newAction`),
              loading: false,
              error: error,
            },
          },
        },
      };
    }

    case "INVALIDATE_CREATE_NEW_ACTION": {
      const stageId = _.get(action, "payload") || "";

      if (!stageId) {
        return {
          ...state,
        };
      }

      return {
        ...state,
        actions: {
          ..._.get(state, "actions"),
          [stageId]: {
            ..._.get(state, `actions[${stageId}]`),
            newAction: {
              item: {
                ..._.get(state, `actions[${stageId}].newAction.item`, {}),
                actionItem: "",
                assets: [],
                recipients: [],
                dueDate: null,
                completed: false,
                stage: stageId,
              } as DealRoomAction,
              loading: false,
              error: null,
            },
          },
        },
        editingItem: null,
      };
    }

    case "INITIATE_EDIT_ACTION": {
      const stageId = _.get(action, "payload.stageId") || "";
      const actionId = _.get(action, "payload.actionId") || "";

      const editingItem = _.get(state, "editingItem");

      if (!stageId || !actionId) {
        return {
          ...state,
        };
      }

      if (editingItem && _.get(editingItem, "id")) {
        if (actionId !== _.get(editingItem, "id")) {
          return {
            ...state,
            editingItem: {
              ..._.get(state, "editingItem"),
              wiggleKey: _.get(state, "editingItem.wiggleKey", 0) + 1,
            } as { type: "stage" | "action"; id: string; wiggleKey: number },
          };
        }

        return {
          ...state,
        };
      } else {
        const actions = _.get(state, `actions[${stageId}].items`);
        const targetActionIndex = _.findIndex(
          actions,
          (action: DealRoomAction) => action.id === actionId,
        );
        const targetAction = actions[targetActionIndex] ?? null;

        if (targetAction) {
          return {
            ...state,
            actions: {
              ..._.get(state, "actions"),
              [stageId]: {
                ..._.get(state, `actions[${stageId}]`),
                editAction: {
                  ..._.get(state, `actions[${stageId}].editAction`),
                  item: targetAction,
                },
              },
            },
            editingItem: {
              type: "action",
              id: actionId,
              wiggleKey: 0,
            },
          };
        }

        return {
          ...state,
          editingItem: {
            type: "action",
            id: actionId,
            wiggleKey: 0,
          },
        };
      }
    }

    case "INVALIDATE_EDIT_ACTION": {
      const stageId = _.get(action, "payload") || "";

      if (!stageId) {
        return {
          ...state,
        };
      }

      return {
        ...state,
        actions: {
          ..._.get(state, "actions"),
          [stageId]: {
            ..._.get(state, `actions[${stageId}]`),
            editAction: {
              item: null,
              loading: false,
              error: null,
            },
          },
        },
        editingItem: null,
      };
    }

    case "INVALIDATE_EDITING_ITEM": {
      return {
        ...state,
        editingItem: null,
      };
    }

    case "EDIT_ACTION": {
      const stageId = _.get(action, "payload.stageId") || "";
      const actionId = _.get(action, "payload.actionId") || "";
      const changes = _.get(action, "payload.changes") || {};
      const isNewAction = _.get(action, "payload.isNewAction") || false;

      if (!stageId || !actionId || _.size(Object.keys(changes)) === 0) {
        return {
          ...state,
        };
      }

      if (isNewAction) {
        const modifiedNewAction = {
          ..._.get(state, `actions[${stageId}].newAction.item`, {}),
          ...changes,
        };

        return {
          ...state,
          actions: {
            ..._.get(state, "actions"),
            [stageId]: {
              ..._.get(state, `actions[${stageId}]`),
              newAction: {
                ..._.get(state, `actions[${stageId}].newAction`),
                item: {
                  ..._.get(state, `actions[${stageId}].newAction.item`),
                  ...modifiedNewAction,
                },
              },
            },
          },
        };
      }

      const finalState = {
        ...state,
        actions: {
          ..._.get(state, "actions"),
          [stageId]: {
            ..._.get(state, `actions[${stageId}]`),
            editAction: {
              ..._.get(state, `actions[${stageId}].editAction`),
              item: {
                ..._.get(state, `actions[${stageId}].editAction.item`),
                ...changes,
              } as DealRoomAction,
            },
          },
        },
      };

      return finalState;
    }

    case "TOGGLE_SAVE_ACTION_SLIDER": {
      const actionItem = _.get(action, "payload.action");
      const stageId = _.get(action, "payload.stageId");
      const changes = _.get(action, "payload.changes");
      const stakeholdersToBeNotified = _.get(
        action,
        "payload.stakeholdersToBeNotified",
      );

      return {
        ...state,
        saveActionSlider: {
          action: actionItem,
          stageId,
          changes,
          stakeholdersToBeNotified,
        },
      };
    }

    case "SAVE_EDIT_ACTION": {
      const stageId = _.get(action, "payload.stageId");
      const actionId = _.get(action, "payload.actionId");

      if (!stageId || !actionId) {
        return {
          ...state,
        };
      }

      return {
        ...state,
        actions: {
          ..._.get(state, "actions"),
          [stageId]: {
            ..._.get(state, `actions[${stageId}]`),
            editAction: {
              ..._.get(state, `actions[${stageId}].editAction`),
              loading: true,
            },
          },
        },
      };
    }

    case "SAVING_EDIT_ACTION_SUCCEEDED": {
      const stageId = _.get(action, "payload.stageId");
      const actionId = _.get(action, "payload.actionId");
      const updatedAction = _.get(action, "payload.updatedAction");
      const updatedStageInfo = _.get(action, "payload.updatedStageInfo", {});

      if (!stageId || !actionId || !updatedAction) {
        return {
          ...state,
        };
      }

      const actions = _.get(state, `actions[${stageId}].items`, []);
      const modifiedActions = _.map(actions, (action) => {
        if (action.id === actionId) {
          return updatedAction;
        }

        return action;
      });

      const stages = _.get(state, "stages.items", []);
      const targetStageIndex = _.findIndex(
        stages,
        (stage) => stage.id === stageId,
      );

      if (targetStageIndex < 0) {
        return {
          ...state,
        };
      }

      const targetStage = _.get(state, `stages.items[${targetStageIndex}]`, {});
      const updatedTargetStage = {
        ...targetStage,
        ...updatedStageInfo,
      };

      const modifiedStages = _.map(stages, (stage) => {
        if (stage.id === stageId) {
          return updatedTargetStage;
        }

        return stage;
      });

      return {
        ...state,
        actions: {
          ..._.get(state, "actions"),
          [stageId]: {
            ..._.get(state, `actions[${stageId}]`),
            items: [...modifiedActions],
            editAction: {
              item: null,
              loading: false,
              error: null,
            },
          },
        },
        stages: {
          ..._.get(state, "stages"),
          items: [...modifiedStages] as DealRoomStage[],
        },
        editingItem: null,
      };
    }

    case "SAVING_EDIT_ACTION_FAILED": {
      const stageId = _.get(action, "payload.stageId");
      const actionId = _.get(action, "payload.actionId");
      const error = _.get(action, "payload.error");

      if (!stageId || !actionId) {
        return {
          ...state,
        };
      }

      return {
        ...state,
        actions: {
          ..._.get(state, "actions"),
          [stageId]: {
            ..._.get(state, `actions[${stageId}]`),
            editAction: {
              item: null,
              loading: false,
              error,
            },
          },
        },
      };
    }

    case "REORDERING_ACTIONS": {
      const targetStageId = _.get(action, "payload.stageId");
      const updatedActionsInOrder = _.get(
        action,
        "payload.updatedActionsInOrder",
      );

      return {
        ...state,
        actions: {
          ..._.get(state, "actions"),
          [targetStageId]: {
            ..._.get(state, `actions[${targetStageId}]`),
            items: [...updatedActionsInOrder],
            oldOrder: _.get(state, `actions[${targetStageId}].items`, []),
          },
        },
      };
    }

    case "REORDERING_ACTIONS_SUCCEEDED": {
      const targetStageId = _.get(action, "payload.stageId");
      if (!targetStageId) {
        return {
          ...state,
        };
      }

      return {
        ...state,
        actions: {
          ..._.get(state, "actions"),
          [targetStageId]: {
            ..._.get(state, `actions[${targetStageId}]`),
            oldOrder: null,
          },
        },
      };
    }

    case "REORDERING_ACTIONS_FAILED": {
      const targetStageId = _.get(action, "payload.stageId");
      const error = _.get(action, "payload.error");

      if (!targetStageId) {
        return {
          ...state,
        };
      }

      return {
        ...state,
        actions: {
          ..._.get(state, "actions"),
          [targetStageId]: {
            ..._.get(state, `actions[${targetStageId}]`),
            items: [..._.get(state, `actions[${targetStageId}].oldOrder`, [])],
            oldOrder: null,
            error,
          },
        },
      };
    }

    case "COMPLETE_ACTION": {
      const stageId = _.get(action, "payload.stageId");
      const actionId = _.get(action, "payload.actionId");
      const completed = _.get(action, "payload.completed");

      if (!stageId || !actionId) {
        return {
          ...state,
        };
      }

      return {
        ...state,
        completeAction: {
          stageId,
          actionId,
          completed,
          loading: false,
          error: null,
        },
      };
    }

    case "COMPLETING_ACTION": {
      return {
        ...state,
        completeAction: {
          ..._.get(state, "completeAction"),
          loading: true,
        },
      };
    }

    case "COMPLETING_ACTION_SUCCEEDED": {
      const stageId = _.get(action, "payload.stageId");
      const actionId = _.get(action, "payload.actionId");
      const updatedAction = _.get(action, "payload.updatedAction");
      const updatedStageInfo = _.get(action, "payload.updatedStageInfo");

      if (!stageId || !actionId || !updatedAction) {
        return {
          ...state,
        };
      }

      const actions = _.get(state, `actions[${stageId}].items`, []);
      const modifiedActions = _.map(actions, (action) => {
        if (action.id === actionId) {
          return updatedAction;
        }

        return action;
      });

      const stages = _.get(state, "stages.items", []);
      const targetStageIndex = _.findIndex(
        stages,
        (stage) => stage.id === stageId,
      );

      if (targetStageIndex < 0) {
        return {
          ...state,
        };
      }

      const targetStage = _.get(state, `stages.items[${targetStageIndex}]`, {});
      const updatedTargetStage = {
        ...targetStage,
        ...updatedStageInfo,
      };

      const modifiedStages = _.map(stages, (stage) => {
        if (stage.id === stageId) {
          return updatedTargetStage;
        }

        return stage;
      });

      return {
        ...state,
        actions: {
          ..._.get(state, "actions"),
          [stageId]: {
            ..._.get(state, `actions[${stageId}]`),
            items: [...modifiedActions],
          },
        },
        stages: {
          ..._.get(state, "stages"),
          items: [...modifiedStages],
        },
        completeAction: {
          stageId: "",
          actionId: "",
          completed: false,
          loading: false,
          error: null,
        },
      };
    }

    case "COMPLETING_ACTION_FAILED": {
      const stageId = _.get(action, "payload.stageId");
      const actionId = _.get(action, "payload.actionId");
      const error = _.get(action, "payload.error");

      if (!stageId || !actionId) {
        return {
          ...state,
        };
      }

      return {
        ...state,
        actions: {
          ..._.get(state, "actions"),
          [stageId]: {
            ..._.get(state, `actions[${stageId}]`),
          },
        },
        completeAction: {
          ..._.get(state, "completeAction"),
          stageId: "",
          actionId: "",
          loading: false,
          error: error,
        },
      };
    }

    case "INVALIDATE_COMPLETE_ACTION": {
      return {
        ...state,
        completeAction: {
          stageId: "",
          actionId: "",
          completed: false,
          loading: false,
          error: null,
        },
      };
    }

    case "TOGGLE_DELETE_ACTION_SLIDER": {
      const actionItem = _.get(action, "payload.action");
      const stageId = _.get(action, "payload.stageId");

      return {
        ...state,
        deleteActionSlider: {
          action: actionItem,
          stageId,
        },
      };
    }

    case "DELETE_ACTION": {
      const stageId = _.get(action, "payload.stageId");
      const actionId = _.get(action, "payload.actionId");

      if (!stageId || !actionId) {
        return {
          ...state,
        };
      }

      return {
        ...state,
        deleteAction: {
          stageId,
          actionId,
          loading: true,
          error: null,
        },
      };
    }

    case "DELETING_ACTION_SUCCEEDED": {
      const stageId = _.get(action, "payload.stageId");
      const actionId = _.get(action, "payload.actionId");
      const newActionsOrder = _.get(action, "payload.newActionsOrder");
      const completedActions = _.get(action, "payload.completedActions");
      const totalActions = _.get(action, "payload.totalActions");
      const totalAttachments = _.get(action, "payload.totalAttachments");
      const totalRecipients = _.get(action, "payload.totalRecipients");

      if (!stageId || !actionId) {
        return {
          ...state,
        };
      }

      const actions = _.get(state, `actions[${stageId}].items`, []);
      const orderMap = _.fromPairs(newActionsOrder.map((id, i) => [id, i]));

      const modifiedActions = _(actions)
        .filter((action) => orderMap[_.get(action, "id")] !== undefined)
        .sortBy((action) => orderMap[_.get(action, "id")])
        .value();

      const stages = _.get(state, "stages.items", []);
      const targetStageIndex = _.findIndex(
        stages,
        (stage) => stage.id === stageId,
      );

      if (targetStageIndex < 0) {
        return {
          ...state,
        };
      }

      const targetStage = _.get(state, `stages.items[${targetStageIndex}]`);
      const updatedTargetStage = {
        ...targetStage,
        completedActions,
        totalActions,
        totalAttachments,
        totalRecipients,
      };

      const modifiedStages = _.map(stages, (stage) => {
        if (stage.id === stageId) {
          return updatedTargetStage;
        }

        return stage;
      });

      return {
        ...state,
        stages: {
          ..._.get(state, "stages"),
          items: modifiedStages,
        },
        actions: {
          ..._.get(state, "actions"),
          [stageId]: {
            ..._.get(state, `actions[${stageId}]`),
            items: [...modifiedActions],
          },
        },
        deleteAction: {
          stageId: "",
          actionId: "",
          loading: false,
          error: null,
        },
      };
    }

    case "DELETING_ACTION_FAILED": {
      const stageId = _.get(action, "payload.stageId");
      const actionId = _.get(action, "payload.actionId");
      const error = _.get(action, "payload.error");

      if (!stageId || !actionId) {
        return {
          ...state,
        };
      }

      return {
        ...state,
        actions: {
          ..._.get(state, "actions"),
          [stageId]: {
            ..._.get(state, `actions[${stageId}]`),
          },
        },
        deleteAction: {
          ..._.get(state, "deleteAction"),
          stageId: "",
          actionId: "",
          loading: false,
          error: error,
        },
      };
    }

    case "INVALIDATE_DELETE_ACTION": {
      return {
        ...state,
        deleteAction: {
          stageId: "",
          actionId: "",
          loading: false,
          error: null,
        },
        deleteActionSlider: {
          action: null,
          stageId: "",
        },
      };
    }

    case "INVALIDATE_COMPLETE_ACTION": {
      return {
        ...state,
        completeAction: {
          stageId: "",
          actionId: "",
          completed: false,
          loading: false,
          error: null,
        },
      };
    }

    case "TOGGLE_ADD_ASSETS_SLIDER": {
      const show = _.get(action, "payload.show");
      const stageId = _.get(action, "payload.stageId");
      const actionId = _.get(action, "payload.actionId");
      const isNewAction = _.get(action, "payload.isNewAction");
      const existingAssets = _.get(action, "payload.existingAssets");

      if (show) {
        return {
          ...state,
          addAssetSlider: {
            show,
            stageId,
            actionId,
            isNewAction,
            existingAssets,
          },
        };
      }

      return {
        ...state,
        addAssetSlider: {
          show: false,
          stageId: "",
          actionId: "",
          isNewAction: false,
          existingAssets: [],
        },
      };
    }

    case "INVALIDATE_ON_GLOBAL_EXIT": {
      const resetActions = (actions: {
        [stageId: string]: ActionsState;
      }): {
        [stageId: string]: ActionsState;
      } => {
        const defaultEditAction = {
          item: null,
          loading: false,
          error: null,
        };

        const resetedActions = Object.fromEntries(
          Object.entries(actions).map(([stageId, actionState]) => [
            stageId,
            {
              ...actionState,
              oldOrder: [],
              newAction: {
                item: {
                  ..._.get(state, `actions[${stageId}].newAction.item`, {}),
                  actionItem: "",
                  assets: [],
                  recipients: [],
                  dueDate: null,
                  completed: false,
                  stage: stageId,
                } as DealRoomAction,
                loading: false,
                error: null,
              },
              editAction: defaultEditAction,
            } as ActionsState,
          ]),
        );

        return resetedActions;
      };

      const resetedActions = resetActions(_.get(state, "actions", {}));

      const stages = _.get(state, "stages.items", []);
      const existingStages = _.filter(
        stages,
        (stage) => _.get(stage, "id") !== "new-stage",
      );

      const resetStages = _.map(existingStages, (stage) => {
        return {
          ...stage,
          isStageAccordionOpen: false,
        };
      });

      return {
        ...state,
        editingItem: null,
        editMode: false,
        isReorderingStages: false,
        deletingStage: {
          id: null,
          loading: false,
          error: null,
        },
        editingStage: {
          id: null,
          item: null,
          loading: false,
          error: null,
        },
        creatingStage: {
          id: null,
          loading: false,
          error: null,
        },
        deleteAction: {
          stageId: "",
          actionId: "",
          loading: false,
          error: null,
        },
        actions: resetedActions,
        stages: {
          ..._.get(state, "stages"),
          items: resetStages,
        },
      };
    }

    default: {
      return {
        ...state,
      };
    }
  }
}
